/*
 *   File : wealth-growth.js
 *   Author : https://evoqins.com
 *   Description : Wealth growth goal detail page
 *   Integrations : NA
 *   Version : 1.0.0
*/

// *********** React Core ***************//
import { useState, useEffect, useRef } from "react";

// *********** Third Party ***************//
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";

// *********** Components ***************//
import { CustomAmountInput, CustomSlider, CustomStepper, CustomTextInput } from "../../Component/Form-Elements";
import { PrimaryButton } from "../../Component/Buttons";
import { Footer } from "../../Component/Footer";
import { BreadCrumbs } from "../../Component/Other";
import { Icon } from "../../Component/Icon";
import { ChangeBasketNameModal } from "../../Component/Modal";
import GoalSummary from "./goal-summary";
import { APIService } from "../../Service";
import { _formatLac, _formatToRupees, _getAchieveByText, _getWealthCorpus, _setGoal } from "../../Helper";

// *********** Assets ***************//
import Loader from "../../Assets/Images/coloured_loader.svg";
import LeftPillarImage from "../../Assets/Images/GoalNew/goal-left-banner.png";
import RightPillarImage from "../../Assets/Images/GoalNew/goal-right-banner.png";
import MixTitleBgImage from "../../Assets/Images/GoalNew/asset-return-card-title-bg.png";
import DeleteChangeIcon from "../../Assets/Images/GoalNew/delete-fund.svg";

const BREADCRUMBS = [
    {
        label: "Home",
        path: "/"
    },
    {
        label: "Life goals",
        path: "/goals"
    },
];

const DEFAULT_LUMPSUM = 0;
const DEFAULT_MONTHLY_SIP = 10000;
const DEFAULT_YEARS = 10;

const MAX_LUMPSUM = 990000000;
const MAX_SIP = 500000;
const LUMPSUM_SLIDER_MAX = 10000000;
const LUMPSUM_STEP = 10000;
const SIP_SLIDER_MAX = 500000;
const SIP_MIN = 500;
const SIP_STEP = 500;
const YEARS_MIN = 1;
const YEARS_MAX = 30;

const MIN_TIMELINE_FILL_PERCENT = 2.7;

// The first planned change can be in the next year, a new one starts 2 years ahead (or at the last year when the goal is shorter)
const MIN_PLANNED_CHANGE_YEAR_OFFSET = 1;
const NEW_PLANNED_CHANGE_YEAR_OFFSET = 2;
const PLANNED_AMOUNT_STEP = 100000;
const PLANNED_AMOUNT_MAX = 100000000;
const MAX_PLANNED_CHANGES = 5;
const INVEST_TYPE = "invest";
const WITHDRAW_TYPE = "withdraw";

const FD_ASSET_MIX_ID = 1;

const ASSET_MIX_OPTIONS = [
    {
        id: FD_ASSET_MIX_ID,
        label: "FD’s",
        return_percentage: 6,
    },
    {
        id: 2,
        label: "Balanced",
        return_percentage: 9,
    },
    {
        id: 3,
        label: "IFSH Index",
        return_percentage: 12,
    },
];

const WealthGrowth = () => {

    const location = useLocation();
    const navigate = useNavigate();

    const currentYear = new Date().getFullYear();

    const [goalId, setGoalId] = useState("");
    const [goalDetail, setGoalDetail] = useState({});
    const [loading, setLoading] = useState(true);

    const [lumpsum, setLumpsum] = useState(DEFAULT_LUMPSUM);
    const [monthlySip, setMonthlySip] = useState(DEFAULT_MONTHLY_SIP);
    const [fundsYears, setFundsYears] = useState(String(DEFAULT_YEARS));
    const [selectedAssetMixId, setSelectedAssetMixId] = useState(FD_ASSET_MIX_ID);
    const [plannedChanges, setPlannedChanges] = useState([
        { id: 1, year: currentYear + MIN_PLANNED_CHANGE_YEAR_OFFSET, type: INVEST_TYPE, amount: 500000 },
    ]);
    const [isSavingGoal, setIsSavingGoal] = useState(false);
    const [savedGoalId, setSavedGoalId] = useState(null);
    const [goalName, setGoalName] = useState("");
    const [showChangeNameModal, setShowChangeNameModal] = useState(false);
    const [isNameUpdating, setIsNameUpdating] = useState(false);
    const [goalSummary, setGoalSummary] = useState(false);

    const nextPlannedChangeId = useRef(2);

    let fundsYearsError = "";
    if (fundsYears !== "" && Number(fundsYears) < YEARS_MIN) {
        fundsYearsError = `Minimum is ${YEARS_MIN} year`;
    }

    useEffect(() => {

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });

        if (location.state !== null) {
            setGoalId(location.state.goal_id);
        } else {
            navigate("/goals");
        };

        if (location.state?.is_summary === false) {
            setGoalSummary(false);
        }

    }, [location]);

    useEffect(() => {
        if (goalId !== "") {
            _getGoalDetails();
        }
    }, [goalId]);

    // The summary step is kept in the url, so that a reload stays on it
    useEffect(() => {
        if (goalSummary === true) {
            navigate(`${location.pathname}#configure`, {
                state: {
                    goal_id: goalId,
                }
            });
        }
    }, [goalSummary]);

    useEffect(() => {
        if (showChangeNameModal) {
            new Modal(document.getElementById("change-basket-name-modal")).show();
        }
    }, [showChangeNameModal]);

    const breadcrumbs = [...BREADCRUMBS, { label: goalDetail.admin_configured?.name }];

    const selectedAssetMix = ASSET_MIX_OPTIONS.find((option) => option.id === selectedAssetMixId);
    const assetMixReturn = selectedAssetMix.return_percentage;

    const years = Number(fundsYears) || 0;
    const endYear = currentYear + years;
    const sipToday = Number(monthlySip) || 0;
    const lumpsumAmount = Number(lumpsum) || 0;

    // The wealth built is worked out year by year from the lumpsum, the SIP and the planned investments and withdrawals
    const goalCost = _getWealthCorpus(lumpsumAmount, sipToday, assetMixReturn, years, plannedChanges, currentYear);

    const goalCostText = _formatLac(goalCost).replace(" ", "");
    const achieveByText = _getAchieveByText(years);

    // The timeline fills as the lumpsum grows towards the goal cost
    let lumpsumProgressPercent = 0;
    if (goalCost > 0) {
        lumpsumProgressPercent = Math.min((lumpsumAmount / goalCost) * 100, 100);
    }
    const timelineFillPercent = Math.max(lumpsumProgressPercent, MIN_TIMELINE_FILL_PERCENT);

    const isMaxPlannedChanges = plannedChanges.length >= MAX_PLANNED_CHANGES;

    //handle back
    const _handleBack = () => {
        navigate("/goals");
    };

    const _handleLumpsumChange = (value) => {
        setLumpsum(value);
    };

    const _handleMonthlySipChange = (value) => {
        setMonthlySip(value);
    };

    const _handleFundsYearsChange = (value) => {
        setFundsYears(String(value));
    };

    const _handleSelectAssetMix = (id) => {
        setSelectedAssetMixId(id);
    };

    const _updatePlannedChange = (id, updates) => {
        setPlannedChanges((changes) => changes.map((change) => change.id === id ? { ...change, ...updates } : change));
    };

    const _handleChangeYear = (change, step) => {
        _updatePlannedChange(change.id, { year: change.year + step });
    };

    const _handleChangeAmount = (change, step) => {
        _updatePlannedChange(change.id, { amount: change.amount + step * PLANNED_AMOUNT_STEP });
    };

    const _handleToggleChangeType = (change) => {
        _updatePlannedChange(change.id, { type: change.type === INVEST_TYPE ? WITHDRAW_TYPE : INVEST_TYPE });
    };

    const _handleAddPlannedChange = () => {
        const newChange = {
            id: nextPlannedChangeId.current,
            year: currentYear + Math.max(Math.min(NEW_PLANNED_CHANGE_YEAR_OFFSET, years), MIN_PLANNED_CHANGE_YEAR_OFFSET),
            type: INVEST_TYPE,
            amount: PLANNED_AMOUNT_STEP,
        };

        nextPlannedChangeId.current += 1;
        setPlannedChanges([...plannedChanges, newChange]);
    };

    const _handleRemovePlannedChange = (id) => {
        setPlannedChanges(plannedChanges.filter((change) => change.id !== id));
    };

    const _handleOpenChangeNameModal = () => {
        setShowChangeNameModal(true);
    };

    const _handleChangeGoalName = (name) => {
        _renameGoal(name, false);
    };

    const _handleSkipGoalName = () => {
        _renameGoal("", true);
    };

    const _handleCloseChangeNameModal = () => {
        setIsNameUpdating(false);
        setShowChangeNameModal(false);
    };

    // API - Rename goal
    const _renameGoal = (name, skip) => {
        let api_version = 3;
        let is_post = true;
        let url = "goal/rename";
        let data = {
            "goal_id": savedGoalId,
            "skip": true,
        };

        if (skip === false) {
            data = {
                "goal_id": savedGoalId,
                "name": name,
                "skip": false,
            };
        }

        setIsNameUpdating(true);

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                if (skip === false) {
                    setGoalName(name);
                }
                document.getElementById("change-basket-name-modal-close")?.click();
                setGoalSummary(true);
            } else {
                toast.dismiss();
                toast.error(response?.message ?? "Something went wrong", { className: "e-toast" });
            }
            setIsNameUpdating(false);
        });
    };

    // API - Save the wealth growth goal, the lumpsum goes as the lumpsum of the goal
    const _handleSaveGoal = () => {
        if (years < YEARS_MIN) {
            toast.dismiss();
            toast.error("Enter the number of years you need the funds in", { className: "e-toast" });
            return;
        }

        if (sipToday === 0 && lumpsumAmount === 0) {
            toast.dismiss();
            toast.error("Either monthly SIP or lumpsum is mandatory", { className: "e-toast" });
            return;
        }

        const firstChangeYear = currentYear + MIN_PLANNED_CHANGE_YEAR_OFFSET;
        const hasChangeOutsideGoalPeriod = plannedChanges.some((change) => change.year < firstChangeYear || change.year > endYear);
        if (hasChangeOutsideGoalPeriod) {
            toast.dismiss();
            toast.error(`Planned changes should be between ${firstChangeYear} and ${endYear}`, { className: "e-toast" });
            return;
        }

        const params = {
            "goal_id": goalId,
            "target": goalCost,
            "sip": sipToday || null,
            "lumpsum": lumpsumAmount || null,
            "sip_tenure": years,
            "lumpsum_select": 1,
            "inflation_select": false,
            "additional_data": {
                "add_lumpsum_now": lumpsumAmount > 0,
                "lumpsum_amount": lumpsumAmount,
                "monthly_sip": sipToday,
                "tenure_years": years,
                "return_rate": assetMixReturn,
                "planned_changes": plannedChanges.map((change) => ({
                    "year": change.year,
                    "type": change.type,
                    "amount": change.amount,
                })),
            },
        };

        setIsSavingGoal(true);

        _setGoal(params).then((response) => {
            if (response.status_code === 200) {
                setSavedGoalId(response.data.goal_id);
                toast.dismiss();
                toast.success("Goal saved", { className: "e-toast" });
                _handleOpenChangeNameModal();
            } else {
                toast.dismiss();
                toast.error(response?.message ?? "Something went wrong", { className: "e-toast" });
            }
            setIsSavingGoal(false);
        });
    };

    //API- Post goal detail
    const _getGoalDetails = () => {
        let is_post = true;
        let url = 'goal/get';
        let data = {
            "goal_id": goalId,
            "is_invested": false
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setGoalDetail(response.data);
                _fillGoalInputs(response.data);

                // Preserving current step if user reloads page
                if (location.hash) {
                    setGoalSummary(true);
                }
            } else {
                setGoalDetail({});
            }
            setLoading(false);
        });
    };

    // Fills the inputs from the saved goal (user_configured)
    const _fillGoalInputs = (goal) => {
        const user_configured = goal.user_configured || {};
        const saved_inputs = user_configured.additional_data || {};

        setLumpsum(saved_inputs.lumpsum_amount || DEFAULT_LUMPSUM);
        setMonthlySip(saved_inputs.monthly_sip || user_configured.sip || DEFAULT_MONTHLY_SIP);
        setFundsYears(String(saved_inputs.tenure_years || DEFAULT_YEARS));

        const saved_asset_mix = ASSET_MIX_OPTIONS.find((option) => option.return_percentage === saved_inputs.return_rate);
        if (saved_asset_mix) {
            setSelectedAssetMixId(saved_asset_mix.id);
        }

        if (Array.isArray(saved_inputs.planned_changes) && saved_inputs.planned_changes.length > 0) {
            const saved_changes = saved_inputs.planned_changes.map((change, index) => ({ ...change, id: index + 1 }));
            nextPlannedChangeId.current = saved_changes.length + 1;
            setPlannedChanges(saved_changes);
        }
    };

    // Stays rendered on the summary step until the modal has finished closing
    const changeNameModal = showChangeNameModal &&
        <ChangeBasketNameModal title="Change goal name"
            skipButtonName="Skip"
            basketName={goalName || goalDetail.admin_configured?.name || ""}
            loading={isNameUpdating}
            onChangeName={_handleChangeGoalName}
            onSkip={_handleSkipGoalName}
            onClose={_handleCloseChangeNameModal} />;

    // Goal summary step
    if (goalSummary) {
        return (
            <>
                <GoalSummary goal_name={goalDetail.admin_configured?.short_name}
                    savedGoalId={savedGoalId}
                    customGoalName={goalName}
                    isLumpsum={lumpsumAmount > 0}
                    tenure={{ min: YEARS_MIN, max: YEARS_MAX }}
                    inflationSelect={false} />

                {changeNameModal}

                <Footer />
            </>
        );
    }

    return (
        <>
            <div className="row min-h-100vh pb-4 pb-sm-5 e-bg-lotion px-4 px-sm-5 ps-lg-4 pe-lg-0 padding-26px-top e-border-radius-TL-BL-24px">

                {loading ?
                    <div className="min-h-100vh text-center mt-5" >
                        <img src={Loader}
                            draggable="false"
                            alt="loader" />
                    </div>
                    :
                    <div className="col-lg-11 p-0 pb-4 pb-sm-5">
                        {/* breadcrumbs */}
                        <BreadCrumbs data={breadcrumbs} />

                        {/* Page title */}
                        <header className="d-flex align-items-center gap-sm-3 gap-2 my-sm-4 my-3 ">
                            <button type="button"
                                className="d-flex p-0 no-border no-outline e-bg-transparent cursor-pointer"
                                aria-label="Back to life goals"
                                onClick={_handleBack}>
                                <Icon icon="arrow-back-left" size={24} className="width-20px-res" />
                            </button>
                            <h1 className="mb-0 e-text-chinese-black e-alt-font-poppins e-font-weight-400 e-font-14-res e-font-16 line-height-24px">
                                {goalDetail.admin_configured?.name}
                            </h1>
                        </header>

                        <div className="row gx-4 gy-4 align-items-start">

                            {/* Wealth plan */}
                            <div className="col-md-6">
                                <section className="retirement-goal-card e-bg-white e-border-radius-16 position-relative overflow-hidden pb-4">
                                    <img src={LeftPillarImage}
                                        width={70}
                                        height={455}
                                        className="retirement-pillar retirement-pillar-left d-none d-sm-block"
                                        draggable="false"
                                        alt="" />
                                    <img src={RightPillarImage}
                                        width={70}
                                        height={455}
                                        className="retirement-pillar retirement-pillar-right d-none d-sm-block"
                                        draggable="false"
                                        alt="" />

                                    <div className="retirement-goal-content position-relative px-sm-4">
                                        <header className="px-0 px-sm-5 pt-sm-3 text-center">
                                            <h2 className="mb-2 e-text-eerie-black e-primary-font-inter e-font-weight-600 e-font-18 line-height-30px">
                                                Build <span className="e-heading-font-ivy e-font-weight-400 e-text-cobalt-ink">wealth</span> for your future
                                            </h2>
                                            <p className="mb-0 e-text-charleston-green e-alt-font-poppins e-font-weight-400 e-font-13 e-font-12-res">
                                                Grow your wealth steadily to achieve your financial goals with confidence.
                                            </p>
                                        </header>

                                        {/* Projection */}
                                        <article className="retirement-projection e-border-1px-baby-blue e-border-radius-28 p-3 p-sm-4 mt-4">
                                            <div className="d-flex justify-content-between gap-3">
                                                <div>
                                                    <p className="margin-6px-bottom e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                                        Invest
                                                    </p>
                                                    <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-800 e-font-16 line-height-20px">
                                                        ₹{_formatToRupees(sipToday)}
                                                        <span className="ms-1 e-text-jet-gray e-font-weight-500 e-font-12 line-height-16px">
                                                            monthly
                                                        </span>
                                                    </p>
                                                </div>
                                                <div className="text-end">
                                                    <p className="margin-6px-bottom e-text-jet-gray e-primary-font-inter e-font-weight-400 e-font-14 line-height-18px">
                                                        Goal cost
                                                    </p>
                                                    <p className="mb-0 e-text-vivid-azure e-primary-font-inter e-font-weight-800 e-font-16 line-height-20px">
                                                        ₹{goalCostText}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="retirement-timeline mt-4"
                                                role="progressbar"
                                                aria-label="Lumpsum compared to the goal cost"
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                                aria-valuenow={Math.round(lumpsumProgressPercent)}>
                                                <span className="retirement-timeline-fill"
                                                    style={{ width: `${timelineFillPercent}%` }}></span>
                                            </div>
                                            <p className="d-flex justify-content-between gap-2 mt-2 mb-0 e-text-dim-ash e-primary-font-inter e-font-weight-400 e-font-14 e-font-12-res line-height-18px">
                                                <span>Start today</span>
                                                <span>
                                                    Achieve by <span className="e-text-ink-gray e-font-weight-500">{achieveByText}</span>
                                                </span>
                                            </p>
                                        </article>
                                    </div>
                                </section>

                                {/* Inputs */}
                                <div className="d-flex flex-column gap-4 mt-3 px-4">
                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                Start with a lumpsum
                                            </span>
                                            <CustomAmountInput variant={4}
                                                className="w-190px flex-shrink-0"
                                                inputClassName="e-bg-white"
                                                borderClassName="e-border-1px-gainsboro-gray"
                                                value={lumpsum}
                                                maxValue={MAX_LUMPSUM}
                                                errorMsg=""
                                                handleChange={_handleLumpsumChange} />
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={0}
                                                max={LUMPSUM_SLIDER_MAX}
                                                step={LUMPSUM_STEP}
                                                defaultValue={lumpsumAmount}
                                                handleChange={_handleLumpsumChange} />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                Monthly SIP
                                            </span>
                                            <CustomAmountInput variant={4}
                                                className="w-190px flex-shrink-0"
                                                inputClassName="e-bg-white"
                                                borderClassName="e-border-1px-gainsboro-gray"
                                                value={monthlySip}
                                                maxValue={MAX_SIP}
                                                errorMsg=""
                                                handleChange={_handleMonthlySipChange} />
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={SIP_MIN}
                                                max={SIP_SLIDER_MAX}
                                                step={SIP_STEP}
                                                defaultValue={sipToday}
                                                handleChange={_handleMonthlySipChange} />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="d-flex justify-content-between align-items-center gap-2">
                                            <span className="e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-16 line-height-20px">
                                                Funds required in
                                            </span>
                                            <span className="w-190px flex-shrink-0">
                                                <CustomTextInput variant={4}
                                                    type="number"
                                                    suffix="Year(s)"
                                                    value={fundsYears}
                                                    maxLength={2}
                                                    maxValue={YEARS_MAX}
                                                    hasBottomMargin={false}
                                                    borderClassName="e-border-1px-gainsboro-gray"
                                                    inputWrapperBorderStyle="e-border-radius-12"
                                                    errorMsg={fundsYearsError}
                                                    successMsg=""
                                                    handleChange={_handleFundsYearsChange} />
                                            </span>
                                        </label>
                                        <div className="h-4 margin-top-20px mb-2">
                                            <CustomSlider type={2}
                                                min={YEARS_MIN}
                                                max={YEARS_MAX}
                                                step={1}
                                                defaultValue={years}
                                                handleChange={_handleFundsYearsChange} />
                                        </div>
                                    </div>
                                </div>

                            </div>

                            {/* Asset mix and planned changes */}
                            <div className="col-md-6">
                                <section className="retirement-mix-card e-border-radius-16 position-relative overflow-hidden px-3 px-sm-4 pt-5 pb-4">
                                    <img src={MixTitleBgImage}
                                        width={529}
                                        height={118}
                                        className="retirement-mix-arc"
                                        draggable="false"
                                        alt="" />

                                    <h2 className="position-relative text-center margin-32px-bottom e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-14-res e-font-16 line-height-20px">
                                        <span className="retirement-mix-highlight e-heading-font-ivy e-font-weight-400 e-text-royal-azure">Pre-retirement</span> asset mix & return
                                    </h2>

                                    <ul className="position-relative d-flex gap-2 gap-sm-3 list-unstyled mb-0">
                                        {
                                            ASSET_MIX_OPTIONS.map((option) => (
                                                <li key={option.id} className="flex-fill">
                                                    <button type="button"
                                                        className={`goal-tab ${option.id === selectedAssetMixId ? "active" : ""} w-100 e-border-radius-8 py-2 px-1 cursor-pointer no-outline e-primary-font-inter`}
                                                        aria-pressed={option.id === selectedAssetMixId}
                                                        onClick={() => _handleSelectAssetMix(option.id)}>
                                                        <span className="d-block e-font-weight-500 e-font-14 e-font-12-res line-height-18px">
                                                            {option.label}
                                                        </span>
                                                        <span className="d-block opacity-8 e-font-weight-400 e-font-12 e-font-10-res line-height-16px">
                                                            {option.return_percentage}% p.a
                                                        </span>
                                                    </button>
                                                </li>
                                            ))
                                        }
                                    </ul>

                                    {/* Planned changes */}
                                    <article className="e-bg-asset-mix-card e-border-radius-16 position-relative padding-16px-all mt-4">
                                        <h3 className="mb-1 e-text-black e-primary-font-inter e-font-weight-600 e-font-12 line-height-16px">
                                            Planned changes
                                        </h3>
                                        <p className="mb-3 e-text-dim-ash e-primary-font-inter e-font-weight-400 e-font-12 line-height-16px">
                                            Add or withdraw investment funds
                                        </p>

                                        <ul className="d-flex flex-column gap-2 list-unstyled mb-0">
                                            {
                                                plannedChanges.map((change) => (
                                                    <li key={change.id} className="d-flex align-items-center gap-2">
                                                        <div className="flex-fill d-flex justify-content-between align-items-center gap-1 gap-sm-2 e-bg-white e-border-radius-12 e-box-shadow-soft-card padding-20px-lr padding-12px-top padding-12px-bottom">
                                                            <CustomStepper value={change.year}
                                                                decreaseLabel="Previous year"
                                                                increaseLabel="Next year"
                                                                isDecreaseDisabled={change.year <= currentYear + MIN_PLANNED_CHANGE_YEAR_OFFSET}
                                                                isIncreaseDisabled={change.year >= endYear}
                                                                onDecrease={() => _handleChangeYear(change, -1)}
                                                                onIncrease={() => _handleChangeYear(change, 1)} />

                                                            <button type="button"
                                                                className="d-flex align-items-center gap-1 p-0 no-border no-outline e-bg-transparent cursor-pointer e-text-eerie-black e-primary-font-inter e-font-weight-500 e-font-10 line-height-16px"
                                                                aria-label={`Switch to ${change.type === INVEST_TYPE ? "withdraw" : "invest"}`}
                                                                onClick={() => _handleToggleChangeType(change)}>
                                                                {change.type === INVEST_TYPE ? "Invest" : "Withdraw"}
                                                                <Icon icon="arrow-swap" size={12} />
                                                            </button>

                                                            <CustomStepper value={_formatLac(change.amount)}
                                                                decreaseLabel="Decrease amount"
                                                                increaseLabel="Increase amount"
                                                                isDecreaseDisabled={change.amount <= PLANNED_AMOUNT_STEP}
                                                                isIncreaseDisabled={change.amount >= PLANNED_AMOUNT_MAX}
                                                                onDecrease={() => _handleChangeAmount(change, -1)}
                                                                onIncrease={() => _handleChangeAmount(change, 1)} />
                                                        </div>

                                                        {
                                                            plannedChanges.length > 1 &&
                                                            <button type="button"
                                                                className="d-flex p-0 no-border no-outline e-bg-transparent cursor-pointer"
                                                                aria-label="Remove planned change"
                                                                onClick={() => _handleRemovePlannedChange(change.id)}>
                                                                <img src={DeleteChangeIcon}
                                                                    width={24}
                                                                    height={24}
                                                                    draggable="false"
                                                                    alt="" />
                                                            </button>
                                                        }
                                                    </li>
                                                ))
                                            }
                                        </ul>

                                        <button type="button"
                                            className={`${isMaxPlannedChanges ? "opacity-5" : "cursor-pointer"} goal-planned-add-btn d-block mx-auto margin-12px-top e-border-radius-6 px-3 py-1 no-outline e-text-cobalt-ink e-primary-font-inter e-font-weight-500 e-font-10 line-height-16px`}
                                            disabled={isMaxPlannedChanges}
                                            onClick={_handleAddPlannedChange}>
                                            Invest or withdraw in a year
                                        </button>
                                    </article>
                                </section>
                            </div>
                        </div>

                        {/* Save */}
                        <div className="col-10 col-sm-6 col-lg-4 mx-auto margin-32px-top">
                            <PrimaryButton name="Save this goal"
                                className="w-100 padding-12px-top padding-12px-bottom e-font-12-res"
                                loading={isSavingGoal}
                                onPress={_handleSaveGoal} />
                        </div>
                    </div>
                }
            </div>

            {changeNameModal}

            {/* footer  */}
            <Footer />
        </>
    )
}
export default WealthGrowth;
